/**
 * MyListsView — list of named prospect lists + drilldown into list contents.
 * May 19, 2026. Additive — new sub-tab inside Prospects.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Plus, Trash2, ChevronLeft, Mail, Phone, FileDown, Users, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export function MyListsView({ api, onAddToContacts }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/prospect-lists');
      setLists(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Could not load lists');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  async function createList() {
    const name = newListName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post('/prospect-lists', { name });
      setNewListName('');
      setNewListOpen(false);
      fetchLists();
      toast.success('List created');
    } catch {
      toast.error('Could not create list');
    } finally {
      setCreating(false);
    }
  }

  async function deleteList(id) {
    try {
      await api.del(`/prospect-lists/${id}`);
      setConfirmDelete(null);
      fetchLists();
      toast.success('List deleted');
    } catch {
      toast.error('Could not delete list');
    }
  }

  async function openList(list) {
    setSelectedList(list);
    setLoadingContacts(true);
    try {
      const data = await api.get(`/prospect-lists/${list.id}/contacts`);
      setContacts(data?.contacts || []);
    } catch {
      toast.error('Could not load contacts');
    } finally {
      setLoadingContacts(false);
    }
  }

  function exportListCSV() {
    if (!contacts.length) return;
    const headers = ['first_name', 'last_name', 'title', 'company', 'city', 'email', 'phone'];
    const rows = [headers.join(',')];
    for (const c of contacts) {
      const row = [
        c.first_name || '', c.last_name || '', c.title || '',
        c.organization_name || '', c.city || '', c.email || '', c.phone || '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
      rows.push(row);
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedList.name.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${contacts.length} contacts`);
  }

  async function removeContact(contactId) {
    try {
      await api.del(`/prospect-lists/${selectedList.id}/contacts/${contactId}`);
      setContacts((c) => c.filter((x) => x.id !== contactId));
      toast.success('Removed from list');
    } catch {
      toast.error('Could not remove');
    }
  }

  // ─────────── Detail view ───────────
  if (selectedList) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setSelectedList(null)} data-testid="my-lists-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to lists
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportListCSV} disabled={!contacts.length} data-testid="my-lists-export-csv">
              <FileDown className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => {
                contacts.forEach((c) => onAddToContacts?.(c));
                toast.success(`Added ${contacts.length} to contacts`);
              }}
              disabled={!contacts.length}
              data-testid="my-lists-add-to-pipeline"
            >
              <Users className="w-4 h-4 mr-1" /> Add all to Pipeline
            </Button>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-xl text-[#1A1A1A]">{selectedList.name}</h3>
          <p className="text-sm text-muted-foreground">{contacts.length} contacts</p>
        </div>

        {loadingContacts ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" />
        ) : contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No contacts in this list yet.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="my-lists-contacts">
            {contacts.map((c) => (
              <Card key={c.id} className="border-[#E8E6E1]" data-testid={`my-lists-contact-${c.id}`}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || '(no name)'}
                      </div>
                      <div className="text-sm text-muted-foreground">{c.title || ''}</div>
                      <div className="text-sm font-medium">{c.organization_name || ''}</div>
                      <div className="text-xs text-muted-foreground">{c.city || ''}</div>
                    </div>
                    <button
                      onClick={() => removeContact(c.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove from list"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {c.email && (
                    <div className="text-xs font-mono flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {c.email}
                    </div>
                  )}
                  {c.phone && (
                    <div className="text-xs font-mono flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {c.phone}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─────────── List grid view ───────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl text-[#1A1A1A]">My Lists</h3>
        <Button onClick={() => setNewListOpen(true)} className="bg-[#1A1A1A] hover:bg-[#2A2A2A]" data-testid="my-lists-new">
          <Plus className="w-4 h-4 mr-1" /> New List
        </Button>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" />
      ) : lists.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="my-lists-empty">
          No lists yet. Run a Discover Prospects search and use "Save list" to build your first one.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-lists-grid">
          {lists.map((l) => (
            <Card
              key={l.id}
              className="border-[#E8E6E1] hover:border-[#B89B5E] cursor-pointer transition-colors"
              data-testid={`my-list-${l.id}`}
            >
              <CardContent className="p-4 space-y-2" onClick={() => openList(l)}>
                <div className="flex items-start justify-between">
                  <div className="font-medium text-[#1A1A1A]">{l.name}</div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(l); }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm text-muted-foreground">{l.contact_count || 0} contacts</div>
                {l.updated_at && (
                  <div className="text-xs text-muted-foreground">
                    Updated {new Date(l.updated_at).toLocaleDateString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New List</DialogTitle></DialogHeader>
          <Input
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            placeholder="e.g. Toronto law firms — Q2 outreach"
            data-testid="my-lists-new-name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewListOpen(false)}>Cancel</Button>
            <Button onClick={createList} disabled={creating || !newListName.trim()} data-testid="my-lists-new-create">
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete this list?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            "{confirmDelete?.name}" and all {confirmDelete?.contact_count || 0} contacts in it will be removed. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteList(confirmDelete.id)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
